import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * CountdownTimer - Lucky Number Design System
 *
 * Displays countdown to raffle end with urgency states.
 * Gold (>24h), warning (<24h), error (<1h) with pulse animations.
 */

/**
 * Convert Firestore Timestamp or Date to JS Date
 */
function toDate(date) {
  if (!date) return null;
  // Handle Firestore Timestamp
  if (date?.toDate && typeof date.toDate === 'function') {
    return date.toDate();
  }
  // Handle seconds/nanoseconds format (Firestore Timestamp serialized)
  if (date?.seconds) {
    return new Date(date.seconds * 1000);
  }
  // Handle regular Date or ISO string
  return new Date(date);
}

/**
 * Calculate time remaining
 */
function getTimeRemaining(endDate) {
  const end = toDate(endDate);
  if (!end || isNaN(end.getTime())) {
    return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const total = end - new Date();

  if (total <= 0) {
    return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { total, days, hours, minutes, seconds };
}

export default function CountdownTimer({
  endDate,
  onEnd,
  size = 'md',
  showSeconds = false,
  light = false, // For light backgrounds
  className,
}) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeRemaining(endDate));

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = getTimeRemaining(endDate);
      setTimeLeft(remaining);

      if (remaining.total <= 0) {
        clearInterval(timer);
        onEnd?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate, onEnd]);

  // Determine urgency level
  const isEnded = timeLeft.total <= 0;
  const isUrgent = timeLeft.total > 0 && timeLeft.total < 60 * 60 * 1000; // < 1 hour
  const isEndingSoon = timeLeft.total > 0 && timeLeft.total < 24 * 60 * 60 * 1000; // < 24 hours

  if (isEnded) {
    return (
      <span className={cn(
        'font-mono',
        light ? 'text-midnight/50' : 'text-cream/50',
        className
      )}>
        Ended
      </span>
    );
  }

  const sizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Format the display
  const parts = [];

  if (timeLeft.days > 0) {
    parts.push(`${timeLeft.days}d`);
  }
  if (timeLeft.hours > 0 || timeLeft.days > 0) {
    parts.push(`${timeLeft.hours}h`);
  }
  if (timeLeft.minutes > 0 || timeLeft.hours > 0 || timeLeft.days > 0) {
    parts.push(`${timeLeft.minutes}m`);
  }
  if (showSeconds || (timeLeft.days === 0 && timeLeft.hours === 0)) {
    parts.push(`${timeLeft.seconds}s`);
  }

  // Color classes based on urgency
  const getColorClass = () => {
    if (isUrgent) {
      return 'text-error';
    }
    if (isEndingSoon) {
      return light ? 'text-warning-dark' : 'text-warning';
    }
    return light ? 'text-midnight/70' : 'text-accent';
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono font-bold',
        sizes[size],
        getColorClass(),
        isUrgent && 'animate-pulse-urgency',
        className
      )}
    >
      <ClockIcon className={cn('w-4 h-4', isUrgent && 'text-error')} />
      {parts.join(' ')}
    </span>
  );
}

/**
 * CompactCountdown
 *
 * Minimal countdown for card displays.
 */
export function CompactCountdown({ endDate, light = false, size = 'sm', className }) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeRemaining(endDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeRemaining(endDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  const isEnded = timeLeft.total <= 0;
  const isUrgent = timeLeft.total > 0 && timeLeft.total < 60 * 60 * 1000;
  const isEndingSoon = timeLeft.total > 0 && timeLeft.total < 24 * 60 * 60 * 1000;

  if (isEnded) {
    return (
      <span className={cn(
        'text-xs font-mono',
        light ? 'text-midnight/50' : 'text-cream/50',
        className
      )}>
        Ended
      </span>
    );
  }

  // Compact format
  let display;
  if (timeLeft.days > 0) {
    display = `${timeLeft.days}d ${timeLeft.hours}h`;
  } else if (timeLeft.hours > 0) {
    display = `${timeLeft.hours}h ${timeLeft.minutes}m`;
  } else {
    display = `${timeLeft.minutes}m ${timeLeft.seconds}s`;
  }

  const sizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Color classes based on urgency
  const getColorClass = () => {
    if (isUrgent) {
      return 'text-error';
    }
    if (isEndingSoon) {
      return light ? 'text-warning-dark' : 'text-warning';
    }
    return light ? 'text-midnight/70' : 'text-cream/70';
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono',
        sizes[size],
        getColorClass(),
        isUrgent && 'font-bold animate-countdown-pulse',
        className
      )}
    >
      <ClockIcon className="w-3.5 h-3.5" />
      {display}
    </span>
  );
}

/**
 * LargeCountdown
 *
 * Big countdown display for raffle detail page.
 */
export function LargeCountdown({ endDate, light = false, className }) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeRemaining(endDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeRemaining(endDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  const isEnded = timeLeft.total <= 0;
  const isUrgent = timeLeft.total > 0 && timeLeft.total < 60 * 60 * 1000;
  const isEndingSoon = timeLeft.total > 0 && timeLeft.total < 24 * 60 * 60 * 1000;

  if (isEnded) {
    return (
      <div className={cn('text-center', className)}>
        <p className={cn(
          'text-lg font-display',
          light ? 'text-midnight/60' : 'text-cream/60'
        )}>
          Competition Ended
        </p>
      </div>
    );
  }

  const units = [
    { value: timeLeft.days, label: 'Days' },
    { value: timeLeft.hours, label: 'Hours' },
    { value: timeLeft.minutes, label: 'Mins' },
    { value: timeLeft.seconds, label: 'Secs' },
  ];

  const getColorClass = () => {
    if (isUrgent) return 'text-error';
    if (isEndingSoon) return 'text-warning';
    return 'text-accent';
  };

  return (
    <div className={cn('text-center', isUrgent && 'animate-pulse-urgency', className)}>
      <p className={cn(
        'text-xs font-bold uppercase tracking-wider mb-2',
        light ? 'text-midnight/60' : 'text-cream/60'
      )}>
        Ends In
      </p>
      <div className="flex justify-center gap-3">
        {units.map(({ value, label }) => (
          <div key={label} className="text-center">
            <div className={cn(
              'w-14 h-14 rounded-lg flex items-center justify-center',
              'font-mono text-2xl font-bold',
              light ? 'bg-midnight/10' : 'bg-midnight-lighter',
              getColorClass()
            )}>
              {String(value).padStart(2, '0')}
            </div>
            <p className={cn(
              'text-2xs mt-1 uppercase tracking-wider',
              light ? 'text-midnight/50' : 'text-cream/50'
            )}>
              {label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
